import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { inputCls, type Opt } from './finance-shared'
import { PlnBloco, stickyTh } from './PanelShell'
import { NeuSwitch } from './NeuSwitch'
import { FiPlus, FiEdit2, FiTrash2, FiLoader, FiSliders, FiToggleLeft, FiToggleRight, FiUploadCloud } from 'react-icons/fi'
import { ImportarRegrasCsv } from './ImportarRegrasCsv'
import ConfirmDialog from './ConfirmDialog'

// Administração das regras que alimentam a sugestão automática de categoria na
// Conciliação bancária (suggestCategory() em routes/reconciliation.cjs). Regra =
// palavras-chave (contains, separadas por vírgula) + direção → categoria. O
// financeiro ajusta os padrões aqui, sem precisar de deploy.

type RuleKind = 'debito' | 'credito' | 'ambos'
const KIND_LABEL: Record<RuleKind, string> = { debito: 'Débito', credito: 'Crédito', ambos: 'Ambos' }

interface Rule {
  id: number; pattern: string; legacy_category_code: string | null
  category_id: number | null; category_name: string | null
  kind: RuleKind; sort_order: number; is_active: 0 | 1; auto_create: 0 | 1
}

export function ReconciliationRules() {
  const { authenticatedFetch } = useAuth()
  const [rules, setRules] = useState<Rule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; rule?: Rule } | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [busy, setBusy] = useState<Record<number, boolean>>({})

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await authenticatedFetch('/api/reconciliation-rules')
      const d = await r.json()
      if (d.success) setRules(d.rules)
      else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch])

  useEffect(() => { load() }, [load])

  const toggleActive = async (rule: Rule) => {
    setBusy(b => ({ ...b, [rule.id]: true }))
    try {
      await authenticatedFetch(`/api/reconciliation-rules/${rule.id}`, { method: 'PUT', body: JSON.stringify({ is_active: rule.is_active ? 0 : 1 }) })
      await load()
    } finally { setBusy(b => ({ ...b, [rule.id]: false })) }
  }

  const toggleAutoCreate = async (rule: Rule) => {
    if (!rule.auto_create && !rule.category_id) { alert('Selecione uma categoria antes de ligar a conciliação automática.'); return }
    setBusy(b => ({ ...b, [rule.id]: true }))
    try {
      await authenticatedFetch(`/api/reconciliation-rules/${rule.id}`, { method: 'PUT', body: JSON.stringify({ auto_create: rule.auto_create ? 0 : 1 }) })
      await load()
    } finally { setBusy(b => ({ ...b, [rule.id]: false })) }
  }

  // Liga/desliga a coluna inteira de uma vez (POST /bulk-toggle). Pro Auto-criar,
  // só regras COM categoria contam — o backend pula as sem categoria e devolve
  // `skipped` pra avisar aqui. Confirmação pelo ConfirmDialog do app, não o
  // confirm() nativo do navegador.
  const [bulkBusy, setBulkBusy] = useState<null | 'is_active' | 'auto_create'>(null)
  const [bulkConfirm, setBulkConfirm] = useState<null | { field: 'is_active' | 'auto_create'; value: 0 | 1; count: number; skippable: number }>(null)
  const askBulkToggle = (field: 'is_active' | 'auto_create') => {
    const eligible = field === 'auto_create' ? rules.filter(r => r.category_id) : rules
    if (!eligible.length) { alert('Nenhuma regra com categoria vinculada — o auto-criar exige categoria.'); return }
    const allOn = eligible.every(r => (field === 'auto_create' ? r.auto_create : r.is_active))
    const value: 0 | 1 = allOn ? 0 : 1
    setBulkConfirm({ field, value, count: value ? eligible.length : rules.length, skippable: field === 'auto_create' && value ? rules.length - eligible.length : 0 })
  }
  const doBulkToggle = async () => {
    if (!bulkConfirm) return
    const { field, value } = bulkConfirm
    setBulkConfirm(null)
    setBulkBusy(field)
    try {
      const r = await authenticatedFetch('/api/reconciliation-rules/bulk-toggle', {
        method: 'POST', body: JSON.stringify({ field, value }),
      })
      const d = await r.json()
      if (!d.success) { alert(d.error || 'Falha ao alterar em massa'); return }
      await load()
    } catch { alert('Erro de conexão') }
    finally { setBulkBusy(null) }
  }

  const remove = async (rule: Rule) => {
    if (!window.confirm(`Excluir a regra "${rule.pattern}"?`)) return
    const r = await authenticatedFetch(`/api/reconciliation-rules/${rule.id}`, { method: 'DELETE' })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Não foi possível excluir'); return }
    load()
  }

  return (
    // PLANOS (21/10/2026): a aba vira UM bloco com título; os botões vão para o
    // canto do cabeçalho e a explicação (antes 12px solta no chão) vira uma
    // faixa de leitura entre o cabeçalho e a tabela.
    <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
      {error && <div className="shrink-0 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400">{error}</div>}

      <PlnBloco
        className="flex-1 min-h-[280px]"
        titulo="Regras de conciliação"
        nota={loading ? undefined : `${rules.length} regra${rules.length === 1 ? '' : 's'} · ${rules.filter(r => r.is_active).length} ativa${rules.filter(r => r.is_active).length === 1 ? '' : 's'} · ${rules.filter(r => r.auto_create).length} com auto-criar`}
        extra={
          <>
            <button
              onClick={() => setShowImport(true)}
              className="neu-convex inline-flex items-center gap-2 h-9 rounded-lg border border-border text-sm font-medium px-3 text-foreground hover:bg-secondary"
            >
              <FiUploadCloud className="w-4 h-4" /> Importar regras
            </button>
            <button
              onClick={() => setModal({ mode: 'create' })}
              className="neu-relief inline-flex items-center gap-2 h-9 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3 hover:opacity-90"
            >
              <FiPlus className="w-4 h-4" /> Nova regra
            </button>
          </>
        }
      >
        <p className="shrink-0 px-[18px] py-3 text-[13px] leading-relaxed text-muted-foreground border-b border-border/60 max-w-4xl">
          Quando um movimento do extrato não bate com nenhum título aberto, o sistema sugere a categoria
          comparando a descrição do banco com estas regras. Palavras-chave separadas por vírgula casam por
          "contém", sem diferenciar maiúsculas. Regras com "Auto-criar" ligado criam e baixam o título sozinhas
          no import (ex.: tarifa bancária).
        </p>

      {/* Tabela — única região com scroll; cabeçalho sticky (padrão PanelShell) */}
      <div className="neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Palavras-chave</th>
              <th className={`px-4 py-2.5 font-medium w-24 ${stickyTh}`}>Direção</th>
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Categoria</th>
              <th className={`px-4 py-2.5 font-medium w-20 text-right ${stickyTh}`}>Ordem</th>
              <th className={`px-4 py-2.5 font-medium w-28 text-center ${stickyTh}`}>
                <span className="inline-flex items-center gap-1">
                  Ativa
                  <button disabled={!rules.length || bulkBusy !== null} onClick={() => askBulkToggle('is_active')}
                    title={rules.length && rules.every(r => r.is_active) ? 'Desativar todas as regras de uma vez' : 'Ativar todas as regras de uma vez'}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-40">
                    {bulkBusy === 'is_active' ? <FiLoader className="w-4 h-4 animate-spin" />
                      : rules.length && rules.every(r => r.is_active) ? <FiToggleRight className="w-4 h-4 text-primary" /> : <FiToggleLeft className="w-4 h-4" />}
                  </button>
                </span>
              </th>
              <th className={`px-4 py-2.5 font-medium w-28 text-center ${stickyTh}`}>
                <span className="inline-flex items-center gap-1">
                  Auto-criar
                  <button disabled={!rules.length || bulkBusy !== null} onClick={() => askBulkToggle('auto_create')}
                    title={rules.some(r => r.category_id) && rules.filter(r => r.category_id).every(r => r.auto_create) ? 'Desligar o auto-criar de todas as regras de uma vez' : 'Ligar o auto-criar de todas as regras com categoria de uma vez'}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-40">
                    {bulkBusy === 'auto_create' ? <FiLoader className="w-4 h-4 animate-spin" />
                      : rules.some(r => r.category_id) && rules.filter(r => r.category_id).every(r => r.auto_create) ? <FiToggleRight className="w-4 h-4 text-amber-500" /> : <FiToggleLeft className="w-4 h-4" />}
                  </button>
                </span>
              </th>
              <th className={`px-4 py-2.5 font-medium w-24 text-right ${stickyTh}`}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : rules.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Nenhuma regra cadastrada.</td></tr>
            ) : rules.map(r => (
              <tr key={r.id} className={`border-b border-border/60 last:border-0 hover:bg-secondary/40 ${!r.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-2.5 text-foreground">{r.pattern}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{KIND_LABEL[r.kind]}</td>
                <td className="px-4 py-2.5">
                  {r.category_name
                    ? <span className="text-foreground">{r.category_name}</span>
                    : <span className="text-amber-600 dark:text-amber-400 text-xs">sem categoria vinculada</span>}
                </td>
                <td className="px-4 py-2.5 text-right text-muted-foreground">{r.sort_order}</td>
                <td className="px-4 py-2.5 text-center">
                  <NeuSwitch on={r.is_active} disabled={busy[r.id]} onClick={() => toggleActive(r)}
                    title={r.is_active ? 'Desativar' : 'Ativar'} />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <NeuSwitch on={r.auto_create} disabled={busy[r.id]} onClick={() => toggleAutoCreate(r)} tone="amber"
                    title={r.auto_create ? 'Desligar: não cria título sozinho' : 'Ligar: cria e baixa o título sozinho quando não há título candidato pra casar'} />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Editar" onClick={() => setModal({ mode: 'edit', rule: r })}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    <button title="Excluir" onClick={() => remove(r)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </PlnBloco>

      {bulkConfirm && (
        <ConfirmDialog
          isOpen
          type={bulkConfirm.field === 'auto_create' && bulkConfirm.value ? 'warning' : 'info'}
          title={`${bulkConfirm.value ? 'Ligar' : 'Desligar'} "${bulkConfirm.field === 'auto_create' ? 'Auto-criar' : 'Ativa'}" em massa`}
          message={`${bulkConfirm.value ? 'Ligar' : 'Desligar'} "${bulkConfirm.field === 'auto_create' ? 'Auto-criar' : 'Ativa'}" em ${bulkConfirm.count} regra(s) de uma vez?`}
          details={[
            ...(bulkConfirm.field === 'auto_create' && bulkConfirm.value
              ? ['Movimentos que baterem nessas regras sem título candidato terão o título criado e baixado sozinho nos próximos imports.']
              : []),
            ...(bulkConfirm.skippable > 0
              ? [`${bulkConfirm.skippable} regra(s) sem categoria vinculada ficarão de fora — o auto-criar exige categoria.`]
              : []),
          ]}
          confirmText={bulkConfirm.value ? 'Ligar todas' : 'Desligar todas'}
          onConfirm={doBulkToggle}
          onCancel={() => setBulkConfirm(null)}
        />
      )}

      {modal && (
        <RuleModal state={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load() }} />
      )}

      {showImport && (
        <ImportarRegrasCsv onClose={() => setShowImport(false)} onImported={load} />
      )}
    </div>
  )
}

function RuleModal({ state, onClose, onSaved }: {
  state: { mode: 'create' | 'edit'; rule?: Rule }; onClose: () => void; onSaved: () => void
}) {
  const { authenticatedFetch } = useAuth()
  const rule = state.rule
  const [pattern, setPattern] = useState(rule?.pattern || '')
  const [kind, setKind] = useState<RuleKind>(rule?.kind || 'debito')
  const [categoryId, setCategoryId] = useState<number | ''>(rule?.category_id ?? '')
  const [autoCreate, setAutoCreate] = useState(rule?.auto_create === 1)
  const [sortOrder, setSortOrder] = useState(rule?.sort_order ?? 0)
  const [cats, setCats] = useState<Opt[]>([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // 'ambos' pode categorizar tanto receita quanto despesa — mostra as duas listas.
  useEffect(() => {
    const catKind = kind === 'credito' ? 'receita' : kind === 'debito' ? 'despesa' : ''
    authenticatedFetch(`/api/categories${catKind ? `?kind=${catKind}` : ''}`).then(r => r.json()).then(d => {
      if (d.success) setCats(d.categories.map((c: { id: number; name: string }) => ({ id: c.id, name: c.name })))
    })
  }, [authenticatedFetch, kind])

  const save = async () => {
    if (!pattern.trim()) { setErr('Informe ao menos uma palavra-chave.'); return }
    if (autoCreate && !categoryId) { setErr('Selecione uma categoria antes de ligar a conciliação automática.'); return }
    setSaving(true); setErr(null)
    try {
      const body = { pattern: pattern.trim(), kind, category_id: categoryId || null, auto_create: autoCreate, sort_order: sortOrder }
      const r = state.mode === 'edit'
        ? await authenticatedFetch(`/api/reconciliation-rules/${rule!.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await authenticatedFetch('/api/reconciliation-rules', { method: 'POST', body: JSON.stringify(body) })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha ao salvar'); return }
      onSaved()
    } catch { setErr('Erro de conexão') }
    finally { setSaving(false) }
  }

  return (
    <SideModal
      title={state.mode === 'edit' ? 'Editar regra' : 'Nova regra de conciliação'}
      subtitle="Sugestão automática de categoria"
      icon={FiSliders}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="neu-tab h-9 px-3.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="neu-relief inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Salvar
          </button>
        </>
      }
    >
      {/* PLANOS (21/10/2026): os campos em duas caixas de papel com respiro —
          "o que casa" (palavras, direção, categoria) e "o que faz" (automático,
          prioridade). Dicas em 12.5px, nunca abaixo. */}
      {err && <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400">{err}</div>}
      <section className="neu-card rounded-xl border border-border bg-card p-4 space-y-4">
      <h4 className="text-[15px] font-semibold text-foreground">O que a regra reconhece</h4>
      <label className="block">
        <span className="text-sm font-medium text-foreground">Palavras-chave*</span>
        <input value={pattern} onChange={e => setPattern(e.target.value)} className={inputCls + ' mt-1'}
          placeholder="Ex.: cob intern, cob compe" />
        <span className="block mt-1 text-[12.5px] text-muted-foreground">Separe por vírgula. Casa se a descrição do banco contiver qualquer uma delas.</span>
      </label>
      <label className="block">
        <span className="text-sm font-medium text-foreground">Direção</span>
        <select value={kind} onChange={e => setKind(e.target.value as RuleKind)} className={inputCls + ' mt-1'}>
          <option value="debito">Débito</option>
          <option value="credito">Crédito</option>
          <option value="ambos">Ambos</option>
        </select>
      </label>
      <label className="block">
        <span className="text-sm font-medium text-foreground">Categoria</span>
        <select value={categoryId} onChange={e => setCategoryId(e.target.value ? Number(e.target.value) : '')} className={inputCls + ' mt-1'}>
          <option value="">Sem categoria (só marca o padrão como conhecido)</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      </section>
      <section className="neu-card rounded-xl border border-border bg-card p-4 space-y-4">
      <h4 className="text-[15px] font-semibold text-foreground">O que ela faz</h4>
      <label className="flex items-start gap-3 cursor-pointer">
        <span className={`neu-switch mt-0.5 relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${autoCreate ? 'bg-amber-500' : 'neu-switch-off bg-muted'}`}
          onClick={() => setAutoCreate(v => !v)}>
          <span className={`neu-knob inline-block h-4 w-4 mt-0.5 rounded-full bg-white transition-transform ${autoCreate ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </span>
        <span>
          <span className="block text-sm font-medium text-foreground">Conciliar automaticamente</span>
          <span className="block mt-0.5 text-[12.5px] text-muted-foreground">
            Quando o movimento bater com esta regra e não houver nenhum título candidato pra casar, cria e baixa o título sozinho no import (ex.: tarifa bancária). Exige categoria selecionada.
          </span>
        </span>
      </label>
      <label className="block">
        <span className="text-sm font-medium text-foreground">Ordem de prioridade</span>
        <input type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} className={inputCls + ' mt-1'} />
        <span className="block mt-1 text-[12.5px] text-muted-foreground">Quando várias regras casam com o mesmo extrato, vence a de <strong>palavra-chave mais longa</strong> (a mais específica) — não a de menor ordem. A ordem só desempata entre palavras-chave de mesmo tamanho.</span>
      </label>
      </section>
    </SideModal>
  )
}
